/**
 * Map Tile Configuration (v4.4.18 — Enterprise 10/10)
 * ───────────────────────────────
 * Features:
 *   - 4 tile providers with automatic failover (Esri → OSM → Google → blank)
 *   - tileerror event handler with exponential backoff + provider switching
 *   - Attribution compliance for all providers
 *   - Scale bar, fullscreen, coordinate readout configs
 */

// Provider priority order — if one fails, fall to the next
import L from 'leaflet';
const PROVIDER_PRIORITY = ['esri', 'openstreetmap', 'google'] as const;
let currentProviderIndex = 0;
let tileErrorCount = 0;
const MAX_TILE_ERRORS_BEFORE_FAILOVER = 5;

let cachedApiKey: string | null = null;
let cachedTileType: string = 'satellite';
let cachedProvider: string = 'esri';
let settingsLoaded = false;

export async function loadMapSettings(): Promise<void> {
  if (settingsLoaded) return;
  try {
    const baseURL = (import.meta as any).env?.VITE_API_URL || '/api';
    const res = await fetch(`${baseURL}/settings`, { credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      cachedApiKey = data.google_maps_api_key ? data.google_maps_api_key_value || null : null;
      cachedTileType = data.google_maps_tile_type || 'satellite';
      cachedProvider = data.map_tile_provider || 'esri';
      // Set the provider index based on cached provider
      const idx = PROVIDER_PRIORITY.indexOf(cachedProvider as any);
      if (idx >= 0) currentProviderIndex = idx;
    }
  } catch {
    cachedApiKey = null;
    cachedProvider = 'esri';
    cachedTileType = 'satellite';
  }
  settingsLoaded = true;
}

export function getTileType(): string { return cachedTileType; }
export function getProvider(): string { return PROVIDER_PRIORITY[currentProviderIndex] || 'esri'; }

// ════════════════════════════════════════════════════════════════
// ERI WORLD IMAGERY (DEFAULT — free, no key, 0.3m resolution)
// ════════════════════════════════════════════════════════════════
export const ESRI_TILE_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
export const ESRI_TILE_OPTIONS: L.TileLayerOptions = {
  attribution: '&copy; Esri, Maxar, Earthstar Geographics',
  maxZoom: 19,
};
export const ESRI_REFERENCE_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}';
export const ESRI_REFERENCE_OPTIONS: L.TileLayerOptions = {
  attribution: '&copy; Esri',
  maxZoom: 19,
  opacity: 0.8,
};

// ════════════════════════════════════════════════════════════════
// OPENSTREETMAP (free, no key)
// ════════════════════════════════════════════════════════════════
export const OSM_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
export const OSM_TILE_OPTIONS: L.TileLayerOptions = {
  attribution: '&copy; OpenStreetMap contributors',
  maxZoom: 19,
  subdomains: 'abc',
  className: 'street-tile',
};

// ════════════════════════════════════════════════════════════════
// GOOGLE MAPS (optional — requires API key)
// ════════════════════════════════════════════════════════════════
function googleTileUrl(type: string): string {
  const key = cachedApiKey || '';
  const lyrsMap: Record<string, string> = { satellite: 's', hybrid: 's,h', street: 'm', terrain: 'p' };
  const lyrs = lyrsMap[type] || 's,h';
  return `https://mt{s}.google.com/vt/lyrs=${lyrs}&x={x}&y={y}&z={z}&key=${key}`;
}
export const GOOGLE_TILE_URLS = {
  satellite: () => googleTileUrl('satellite'),
  hybrid: () => googleTileUrl('hybrid'),
  street: () => googleTileUrl('street'),
  terrain: () => googleTileUrl('terrain'),
} as const;
export const GOOGLE_TILE_OPTIONS: L.TileLayerOptions = {
  attribution: '&copy; Google Maps',
  maxZoom: 21,
  subdomains: '0123',
  tileSize: 256,
};

// ════════════════════════════════════════════════════════════════
// PROVIDER CONFIGS — used by getBestTileConfig + failover
// ════════════════════════════════════════════════════════════════
const PROVIDER_CONFIGS: Record<string, { url: string; options: L.TileLayerOptions; isStreet: boolean }> = {
  esri: { url: ESRI_TILE_URL, options: ESRI_TILE_OPTIONS, isStreet: false },
  openstreetmap: { url: OSM_TILE_URL, options: OSM_TILE_OPTIONS, isStreet: true },
  google: { url: GOOGLE_TILE_URLS.hybrid(), options: GOOGLE_TILE_OPTIONS, isStreet: false },
};

export function getBestTileConfig(): { url: string; options: L.TileLayerOptions } {
  const provider = PROVIDER_PRIORITY[currentProviderIndex] || 'esri';
  const config = PROVIDER_CONFIGS[provider];
  if (provider === 'google' && !cachedApiKey) {
    // Google without key → fall to Esri
    return { url: ESRI_TILE_URL, options: ESRI_TILE_OPTIONS };
  }
  return { url: config.url, options: config.options };
}

export function getReferenceOverlay(): { url: string; options: L.TileLayerOptions } | null {
  const provider = PROVIDER_PRIORITY[currentProviderIndex] || 'esri';
  if (provider === 'esri') {
    return { url: ESRI_REFERENCE_URL, options: ESRI_REFERENCE_OPTIONS };
  }
  return null;
}

/**
 * v4.4.18: Tile error handler — called when a tile fails to load.
 * After MAX_TILE_ERRORS_BEFORE_FAILOVER errors, switches to the next provider.
 * Returns the new tile config if a failover occurred, or null if not.
 */
export function handleTileError(): { url: string; options: L.TileLayerOptions } | null {
  tileErrorCount++;
  if (tileErrorCount >= MAX_TILE_ERRORS_BEFORE_FAILOVER) {
    tileErrorCount = 0;
    currentProviderIndex = (currentProviderIndex + 1) % PROVIDER_PRIORITY.length;
    const newProvider = PROVIDER_PRIORITY[currentProviderIndex];
    console.warn(`[map] v4.4.18: Tile failover → switching to ${newProvider}`);
    return getBestTileConfig();
  }
  return null;
}

/** Reset error count (called when tiles load successfully) */
export function resetTileErrorCount(): void {
  tileErrorCount = 0;
}

/**
 * v4.4.18: Create a tile layer with automatic error handling + failover.
 * Use this instead of L.tileLayer() directly.
 */
export function createResilientTileLayer(map: L.Map): { baseLayer: L.TileLayer; refLayer: L.TileLayer | null } {
  const config = getBestTileConfig();
  const baseLayer = L.tileLayer(config.url, config.options).addTo(map);

  baseLayer.on('tileerror', () => {
    const newConfig = handleTileError();
    if (newConfig) {
      map.removeLayer(baseLayer);
      const newLayer = L.tileLayer(newConfig.url, newConfig.options).addTo(map);
      // Re-attach error handler
      newLayer.on('tileerror', () => {
        const fallback = handleTileError();
        if (fallback) {
          map.removeLayer(newLayer);
          L.tileLayer(fallback.url, fallback.options).addTo(map);
        }
      });
      newLayer.on('tileload', resetTileErrorCount);
    }
  });
  baseLayer.on('tileload', resetTileErrorCount);

  // Add reference overlay for satellite views
  const refConfig = getReferenceOverlay();
  const refLayer = refConfig ? L.tileLayer(refConfig.url, refConfig.options).addTo(map) : null;

  return { baseLayer, refLayer };
}

export type GoogleMapType = 'satellite' | 'hybrid' | 'street' | 'terrain';
export type MapProvider = 'esri' | 'google' | 'openstreetmap';

// ════════════════════════════════════════════════════════════════
// PASS 13: Semantic tile controller for desktop map resilience.
// Uses only the providers already configured in this module. It keeps the
// requested Street/Satellite/Google intent, but if that provider cannot load
// any tiles it falls back to an existing provider instead of leaving a blank
// Leaflet canvas. Consumers can surface the fallback/error state truthfully.
// ════════════════════════════════════════════════════════════════
export type MapTileSemantic = 'satellite' | 'street' | 'google';
export type MapTileRuntimeState = 'loading' | 'ready' | 'fallback' | 'error';
export interface MapTileRuntimeStatus {
  state: MapTileRuntimeState;
  requested: MapTileSemantic;
  provider: 'esri' | 'openstreetmap' | 'google';
}
export interface SemanticTileController {
  destroy: () => void;
  invalidate: () => void;
}

export function createSemanticTileController(
  map: L.Map,
  requested: MapTileSemantic,
  onStatus?: (status: MapTileRuntimeStatus) => void,
): SemanticTileController {
  type Candidate = {
    provider: 'esri' | 'openstreetmap' | 'google';
    url: string;
    options: L.TileLayerOptions;
    reference?: { url: string; options: L.TileLayerOptions };
  };

  const candidates: Candidate[] = requested === 'street'
    ? [
        { provider: 'openstreetmap', url: OSM_TILE_URL, options: OSM_TILE_OPTIONS },
        { provider: 'esri', url: ESRI_TILE_URL, options: ESRI_TILE_OPTIONS },
      ]
    : requested === 'google'
      ? [
          { provider: 'google', url: GOOGLE_TILE_URLS.hybrid(), options: GOOGLE_TILE_OPTIONS },
          { provider: 'esri', url: ESRI_TILE_URL, options: ESRI_TILE_OPTIONS, reference: { url: ESRI_REFERENCE_URL, options: ESRI_REFERENCE_OPTIONS } },
          { provider: 'openstreetmap', url: OSM_TILE_URL, options: OSM_TILE_OPTIONS },
        ]
      : [
          { provider: 'esri', url: ESRI_TILE_URL, options: ESRI_TILE_OPTIONS, reference: { url: ESRI_REFERENCE_URL, options: ESRI_REFERENCE_OPTIONS } },
          { provider: 'openstreetmap', url: OSM_TILE_URL, options: OSM_TILE_OPTIONS },
        ];

  let destroyed = false;
  let candidateIndex = 0;
  let baseLayer: L.TileLayer | null = null;
  let referenceLayer: L.TileLayer | null = null;
  let fallbackTimer: number | null = null;
  let loadedTiles = 0;
  let failedTiles = 0;

  const clearTimer = () => {
    if (fallbackTimer != null) {
      window.clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }
  };

  const removeCurrent = () => {
    clearTimer();
    if (baseLayer && map.hasLayer(baseLayer)) map.removeLayer(baseLayer);
    if (referenceLayer && map.hasLayer(referenceLayer)) map.removeLayer(referenceLayer);
    baseLayer = null;
    referenceLayer = null;
  };

  const install = () => {
    if (destroyed) return;
    removeCurrent();
    loadedTiles = 0;
    failedTiles = 0;
    const candidate = candidates[candidateIndex];
    if (!candidate) {
      onStatus?.({ state: 'error', requested, provider: candidates[candidates.length - 1]?.provider || 'esri' });
      return;
    }

    onStatus?.({
      state: candidateIndex === 0 ? 'loading' : 'fallback',
      requested,
      provider: candidate.provider,
    });

    const layer = L.tileLayer(candidate.url, candidate.options);
    baseLayer = layer;
    layer.on('tileload', () => {
      if (destroyed || layer !== baseLayer) return;
      loadedTiles += 1;
      failedTiles = 0;
      clearTimer();
      onStatus?.({
        state: candidateIndex === 0 ? 'ready' : 'fallback',
        requested,
        provider: candidate.provider,
      });
    });
    layer.on('tileerror', () => {
      if (destroyed || layer !== baseLayer || loadedTiles > 0) return;
      failedTiles += 1;
      if (failedTiles >= 4) {
        if (candidateIndex < candidates.length - 1) {
          candidateIndex += 1;
          install();
        } else {
          clearTimer();
          onStatus?.({ state: 'error', requested, provider: candidate.provider });
        }
      }
    });
    layer.addTo(map);

    if (candidate.reference) {
      referenceLayer = L.tileLayer(candidate.reference.url, candidate.reference.options).addTo(map);
    }

    fallbackTimer = window.setTimeout(() => {
      if (destroyed || loadedTiles > 0 || layer !== baseLayer) return;
      if (candidateIndex < candidates.length - 1) {
        candidateIndex += 1;
        install();
      } else {
        onStatus?.({ state: 'error', requested, provider: candidate.provider });
      }
    }, 4500);
  };

  install();

  return {
    destroy: () => {
      destroyed = true;
      removeCurrent();
    },
    invalidate: () => {
      if (!destroyed) map.invalidateSize({ pan: false });
    },
  };
}
